package com.arena_web.ui.components.events

import androidx.compose.runtime.*
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodySRegular
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Video
import org.w3c.dom.DragEvent
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.get

private const val MAX_FILE_BYTES = 30 * 1024 * 1024 // 30 MB
private const val ACCEPT = "image/*,video/*"

/** A picked file held until the user hits Upload. Bytes are NOT read here —
 *  previews render straight from the object URL (instant, even for big
 *  camera shots), and the upload batch reads/compresses the [file] itself. */
data class PendingMediaFile(
    val file: File,
    val name: String,
    val contentType: String,
    val isVideo: Boolean,
    /** Object URL used to render the thumbnail. Ownership transfers to the
     *  upload batch on Upload (it reuses it for the loader tile), otherwise
     *  the popup releases it via [revoke]. */
    val previewUrl: String,
) {
    fun revoke() {
        window.asDynamic().URL.revokeObjectURL(previewUrl)
    }
}

/**
 * Figma 9112:151089 (empty) and 9120:151353 (with selected items) —
 * "Add Media" popup launched from the Media tab on completed events.
 * On phones it renders as a [BottomSheet] instead (Figma 8746:331277 empty /
 * 9112:71629 with selections), sharing the same picker state and upload flow.
 *
 * Lets the admin pick photos / videos via click or drag-and-drop, previews
 * them inline, and uploads them one-by-one through [onUpload] when the
 * Upload Media button is pressed. Files larger than 30 MB are rejected
 * with a browser alert.
 */
@Composable
fun AddMediaPopup(
    isOpen: Boolean,
    onClose: () -> Unit,
    /** Fired once on Upload with everything picked; the popup closes
     *  immediately and the caller (viewModel.uploadMediaBatch) takes over —
     *  loader tiles in the grid, failure report at the end. */
    onUpload: (List<PendingMediaFile>) -> Unit,
    /** Files the last batch couldn't upload — rendered as a report at the
     *  bottom of the sheet (the caller reopens the popup when non-empty and
     *  clears the list on close). */
    failedUploads: List<com.arena_web.core.viewmodel.FailedMediaUpload> = emptyList(),
    /** ✕ on a failed preview — the caller drops that entry from the report. */
    onDismissFailed: (com.arena_web.core.viewmodel.FailedMediaUpload) -> Unit = {},
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val pending = remember { mutableStateListOf<PendingMediaFile>() }
    var dragOver by remember { mutableStateOf(false) }
    // Set on Upload — the object URLs' ownership moved to the upload batch,
    // so the teardown effect must NOT revoke them.
    var handedOff by remember { mutableStateOf(false) }

    // Release any leftover object URLs when the popup tears down so the
    // browser can reclaim the file memory.
    DisposableEffect(Unit) {
        onDispose { if (!handedOff) pending.forEach { it.revoke() } }
    }

    // Entries appear instantly — the system picker closes and each tile
    // shows a loader until its object-URL preview paints. No FileReader
    // here: bytes are read by the upload batch. Oversized files pass
    // through too; the batch rejects them into the failed-uploads report.
    val handleFiles: (List<File>) -> Unit = handler@{ files ->
        if (files.isEmpty()) return@handler
        files.forEach { file ->
            val previewUrl = window.asDynamic().URL.createObjectURL(file) as String
            pending.add(
                PendingMediaFile(
                    file = file,
                    name = file.name,
                    contentType = file.type,
                    isVideo = file.type.startsWith("video/"),
                    previewUrl = previewUrl,
                )
            )
        }
    }

    val openFilePicker: () -> Unit = {
        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = ACCEPT
        input.multiple = true
        input.onchange = {
            val list = input.files
            if (list != null) {
                val picked = (0 until list.length).mapNotNull { list[it] }
                handleFiles(picked)
            }
        }
        input.click()
    }

    // Shared upload action — hands the snapshot to the caller's batch and
    // closes the popup right away; the media grid shows the progress.
    val canUpload = pending.isNotEmpty()
    val startUpload: () -> Unit = {
        if (canUpload) {
            handedOff = true
            onUpload(pending.toList())
            pending.clear()
            onClose()
        }
    }

    // ── Mobile: bottom sheet (Figma 8746:331277 / 9112:71629) ──
    if (isMobile()) {
        // Id.kt's onClose ignores the dismiss while an upload is in flight,
        // so a swipe-down mid-upload can't cancel the request.
        BottomSheet(onDismiss = onClose, contentGap = 16, sidePaddingPx = 22) {
            SpanText(
                t.eventAddMediaTitle,
                modifier = Modifier
                    .fillMaxWidth()
                    .sFontSize(20)
                    .sLineHeight(26)
                    .sLetterSpacing(0.2)
                    .fontWeight(FontWeight.Medium)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )

            // 16 from the sheet's contentGap + 6 = the 22-px title→content
            // gap in the Figma frame.
            Column(modifier = Modifier.fillMaxWidth().sGap(36).sPaddingTop(6)) {
                DropZone(
                    dragOver = dragOver,
                    compact = true,
                    onPick = openFilePicker,
                    onDragOver = { e ->
                        e.preventDefault()
                        dragOver = true
                    },
                    onDragLeave = { dragOver = false },
                    onDrop = { e ->
                        e.preventDefault()
                        dragOver = false
                        val list = e.dataTransfer?.files
                        if (list != null) {
                            val dropped = (0 until list.length).mapNotNull { list[it] }
                            handleFiles(dropped)
                        }
                    },
                )

                if (pending.isNotEmpty() || failedUploads.isNotEmpty()) {
                    Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                        SelectedItemsHeader(
                            count = pending.size + failedUploads.size,
                            compact = true,
                            onClearAll = {
                                pending.forEach { it.revoke() }
                                pending.clear()
                                failedUploads.forEach(onDismissFailed)
                            },
                        )
                        SelectedItemsRow(
                            items = pending,
                            compact = true,
                            onRemove = { idx ->
                                pending[idx].revoke()
                                pending.removeAt(idx)
                            },
                            failed = failedUploads,
                            onDismissFailed = onDismissFailed,
                        )
                        // One error line for the failed tiles — the batch's
                        // losses share the same cause in practice.
                        failedUploads.firstOrNull()?.let { first ->
                            SpanText(
                                first.reason,
                                modifier = Modifier.sFontSize(13).color(Color("#FF4242")),
                            )
                        }
                    }
                }

                // Cancel / Upload Media — two half-width 44-px pills.
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    OutlinePillButton(
                        label = t.commonCancel,
                        enabled = true,
                        onClick = onClose,
                        modifier = mobilePillModifier(),
                    )
                    PrimaryPillButton(
                        label = t.eventUploadMedia,
                        enabled = canUpload,
                        primaryColor = if (pending.isNotEmpty()) sportColors.primary else null,
                        // Figma disabled state — solid primary-disabled fill
                        // (#0A3986) with full-opacity white text, no dimming.
                        disabledBackground = "#0A3986",
                        onClick = startUpload,
                        modifier = mobilePillModifier(),
                    )
                }
            }
        }
        return
    }

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sMaxWidth(700)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(26)
                .sPaddingTop(22)
                .sPaddingLeft(22)
                .sPaddingRight(22)
                .sPaddingBottom(28)
                .sGap(24)
                .overflow(Overflow.Hidden)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("background-color", "var(--l-s2, #09142d)")
                    property("border", "1px solid rgba(60, 121, 241, 0.45)")
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
        ) {
            // Header — title + bottom divider
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPaddingBottom(16)
                    .styleModifier {
                        property("border-bottom", "1px solid rgba(148, 149, 152, 0.15)")
                    }
            ) {
                SpanText(
                    t.eventAddMediaTitle,
                    modifier = Modifier
                        .sFontSize(24)
                        .sLineHeight(32)
                        .sLetterSpacing(0.24)
                        .fontWeight(FontWeight.Medium)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("font-family", "Urbanist, sans-serif") }
                )
            }

            // Upload section: drop zone + (optional) selected items + actions
            Column(modifier = Modifier.fillMaxWidth().sGap(28)) {
                Column(modifier = Modifier.fillMaxWidth().sGap(16)) {
                    DropZone(
                        dragOver = dragOver,
                        onPick = openFilePicker,
                        onDragOver = { e ->
                            e.preventDefault()
                            dragOver = true
                        },
                        onDragLeave = { dragOver = false },
                        onDrop = { e ->
                            e.preventDefault()
                            dragOver = false
                            val list = e.dataTransfer?.files
                            if (list != null) {
                                val dropped = (0 until list.length).mapNotNull { list[it] }
                                handleFiles(dropped)
                            }
                        },
                    )

                    if (pending.isNotEmpty() || failedUploads.isNotEmpty()) {
                        SelectedItemsHeader(
                            count = pending.size + failedUploads.size,
                            onClearAll = {
                                pending.forEach { it.revoke() }
                                pending.clear()
                                failedUploads.forEach(onDismissFailed)
                            },
                        )
                        SelectedItemsRow(
                            items = pending,
                            onRemove = { idx ->
                                pending[idx].revoke()
                                pending.removeAt(idx)
                            },
                            failed = failedUploads,
                            onDismissFailed = onDismissFailed,
                        )
                        failedUploads.firstOrNull()?.let { first ->
                            SpanText(
                                first.reason,
                                modifier = Modifier.sFontSize(14).color(Color("#FF4242")),
                            )
                        }
                    }
                }

                // Action buttons row — Cancel + Upload Media
                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    OutlinePillButton(
                        label = t.commonCancel,
                        enabled = true,
                        onClick = onClose,
                    )

                    PrimaryPillButton(
                        label = t.eventUploadMedia,
                        enabled = canUpload,
                        primaryColor = if (pending.isNotEmpty()) sportColors.primary else null,
                        onClick = startUpload,
                    )
                }
            }
        }
    }
}

@Composable
private fun DropZone(
    dragOver: Boolean,
    onPick: () -> Unit,
    onDragOver: (DragEvent) -> Unit,
    onDragLeave: (Event) -> Unit,
    onDrop: (DragEvent) -> Unit,
    /** Phone sheet sizing — 140-px zone with Body/M + Body/S copy. */
    compact: Boolean = false,
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(if (compact) 140 else 240)
            .sBorderRadius(14)
            .cursor(Cursor.Pointer)
            .onClick { onPick() }
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(194deg, rgba(60, 121, 241, 0.06) 49.5%, rgba(5, 4, 12, 0.06) 105.9%)"
                )
                property(
                    "border",
                    if (dragOver) "2px dashed #3C79F1" else "2px dashed #0C1787"
                )
            },
        contentAlignment = Alignment.Center,
    ) {
        // Drag listeners attached to the underlying DOM element. We bind via
        // a separate hidden ref since Kobweb's Modifier doesn't expose
        // onDragOver/onDrop directly.
        DragBindings(onDragOver = onDragOver, onDragLeave = onDragLeave, onDrop = onDrop)

        Column(
            modifier = Modifier.sGap(if (compact) 6 else 4),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                modifier = Modifier.sGap(if (compact) 8 else 12),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                // 44px circular badge with a + glyph
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .borderRadius(50.percent)
                        .styleModifier {
                            property("border", "0.3px solid transparent")
                            property(
                                "background",
                                "${AppGradients.InteractiveSecondary} padding-box, " +
                                    "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                                    "${AppGradients.ButtonBorderGradientPrimary} border-box"
                            )
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(src = AppIcons.Add, modifier = Modifier.sSize(20))
                }
                SpanText(
                    t.eventDropMediaHere,
                    modifier = (if (compact) Modifier.bodyMRegular() else Modifier.bodyLMedium())
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
            SpanText(
                t.eventMaxFileSize,
                modifier = (if (compact) Modifier.bodySRegular() else Modifier.bodyMRegular())
                    .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
            )
        }
    }
}

@Composable
private fun DragBindings(
    onDragOver: (DragEvent) -> Unit,
    onDragLeave: (Event) -> Unit,
    onDrop: (DragEvent) -> Unit,
) {
    // Attach drag/drop listeners to the parent element after the DOM mounts.
    // We walk up to the nearest positioned ancestor (the drop-zone Box).
    val id = remember { "drop-anchor-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(Unit) {
        val anchor = document.getElementById(id)
        val zone = anchor?.parentElement
        val dragOverHandler: (Event) -> Unit = { e -> onDragOver(e as DragEvent) }
        val dragLeaveHandler: (Event) -> Unit = { e -> onDragLeave(e) }
        val dropHandler: (Event) -> Unit = { e -> onDrop(e as DragEvent) }
        zone?.addEventListener("dragover", dragOverHandler)
        zone?.addEventListener("dragleave", dragLeaveHandler)
        zone?.addEventListener("drop", dropHandler)
        onDispose {
            zone?.removeEventListener("dragover", dragOverHandler)
            zone?.removeEventListener("dragleave", dragLeaveHandler)
            zone?.removeEventListener("drop", dropHandler)
        }
    }
    Box(modifier = Modifier.id(id).sSize(0))
}

@Composable
private fun SelectedItemsHeader(count: Int, onClearAll: () -> Unit, compact: Boolean = false) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier.sGap(6),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            val labelStyle: Modifier =
                if (compact) Modifier.bodyMRegular() else Modifier.bodyLRegular()
            SpanText(
                t.eventSelectedLabel,
                modifier = labelStyle.color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
            Box(
                modifier = Modifier
                    .sSize(if (compact) 3 else 4)
                    .borderRadius(50.percent)
                    .backgroundColor(AppColors.Text.Secondary)
            )
            SpanText(
                count.toString(),
                modifier = labelStyle.color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
        Box(
            modifier = Modifier
                .sHeight(if (compact) 22 else 44)
                .cursor(Cursor.Pointer)
                .onClick { onClearAll() },
            contentAlignment = Alignment.Center,
        ) {
            // Figma mobile 9112:71709 — Body/S muted; desktop keeps Body/M white.
            SpanText(
                t.eventClearAll,
                modifier = if (compact) {
                    Modifier.bodySRegular().color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                } else {
                    Modifier.bodyMRegular().color(AppColors.PrimaryWhite)
                }
            )
        }
    }
}

@Composable
private fun SelectedItemsRow(
    items: List<PendingMediaFile>,
    onRemove: (Int) -> Unit,
    compact: Boolean = false,
    // Failed uploads share the strip — red-outlined tiles with the same ✕.
    failed: List<com.arena_web.core.viewmodel.FailedMediaUpload> = emptyList(),
    onDismissFailed: (com.arena_web.core.viewmodel.FailedMediaUpload) -> Unit = {},
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .sGap(12)
            .styleModifier {
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            },
        verticalAlignment = Alignment.Top,
    ) {
        failed.forEach { fail ->
            FailedThumbnail(fail = fail, onDismiss = { onDismissFailed(fail) }, compact = compact)
        }
        items.forEachIndexed { idx, item ->
            SelectedThumbnail(item = item, onRemove = { onRemove(idx) }, compact = compact)
        }
    }
}

@Composable
private fun SelectedThumbnail(
    item: PendingMediaFile,
    onRemove: () -> Unit,
    /** Phone sheet sizing — Figma 9112:71711: 70×100 tile, 16-px remove badge. */
    compact: Boolean = false,
) {
    // Spinner shows until the object-URL preview actually paints — big
    // camera shots take a beat to decode even from a local blob. Photos keep
    // the proven background-image rendering; a detached JS Image() preloads
    // the blob and flips the flag (covers the cached-already case too).
    var previewLoaded by remember(item.previewUrl) { mutableStateOf(false) }
    if (!item.isVideo) {
        DisposableEffect(item.previewUrl) {
            val img = org.w3c.dom.Image()
            val done: (Event) -> Unit = { previewLoaded = true }
            img.addEventListener("load", done)
            img.addEventListener("error", done)
            img.src = item.previewUrl
            if (img.complete) previewLoaded = true
            onDispose {
                img.removeEventListener("load", done)
                img.removeEventListener("error", done)
            }
        }
    }
    Box(
        modifier = Modifier
            .sWidth(if (compact) 70 else 90)
            .sHeight(if (compact) 100 else 120)
            .styleModifier { property("flex-shrink", "0") }
            .position(Position.Relative),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .sBorderRadius(if (compact) 8 else 10)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("border", "0.5px solid var(--arena-border-top)")
                    property("background-color", "var(--l-s3, #0B1A38)")
                    if (!item.isVideo) {
                        property("background-image", "url('${item.previewUrl}')")
                        property("background-size", "cover")
                        property("background-position", "center")
                        property("background-repeat", "no-repeat")
                    }
                },
            contentAlignment = Alignment.Center,
        ) {
            if (!previewLoaded) {
                com.arena_web.ui.components.widgets.LoadingSpinner(size = 24.px)
            }
            if (item.isVideo) {
                VideoPosterPreview(
                    previewUrl = item.previewUrl,
                    compact = compact,
                    onReady = { previewLoaded = true },
                )
            }
        }

        // Remove badge — Figma 9213:151231 (desktop: 24px circle at top 5 /
        // right 2, gradient border) and 9112:71713 (mobile: 16px circle at
        // top 2.5 / right 3.5, plain white/40 hairline on the sheet fill).
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .sTop(if (compact) 2.5 else 5)
                .sRight(if (compact) 3.5 else 2)
                .sSize(if (compact) 16 else 24)
                .borderRadius(50.percent)
                .cursor(Cursor.Pointer)
                .onClick { onRemove() }
                .styleModifier {
                    if (compact) {
                        property("border", "0.3px solid var(--arena-border-top)")
                        property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                    } else {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    }
                },
            contentAlignment = Alignment.Center,
        ) {
            Icon(src = AppIcons.Close, modifier = Modifier.sSize(if (compact) 8 else 10))
        }
    }
}

/**
 * Poster-frame preview for a picked video. `<video src=blob:...>` doesn't
 * reliably paint a frame on mobile, so `captureVideoPreview` decodes one via
 * canvas; the tile then renders it as a plain image with the play badge and
 * an `m:ss` duration chip. Falls back to the dark fill + badge for codecs
 * the browser can't decode.
 */
@Composable
private fun VideoPosterPreview(
    previewUrl: String,
    compact: Boolean,
    onReady: () -> Unit,
) {
    var posterUrl by remember(previewUrl) { mutableStateOf<String?>(null) }
    var duration by remember(previewUrl) { mutableStateOf<Double?>(null) }
    LaunchedEffect(previewUrl) {
        val info = com.arena_web.utils.captureVideoPreview(previewUrl)
        posterUrl = info.posterUrl
        duration = info.durationSeconds
        onReady()
    }
    DisposableEffect(previewUrl) {
        onDispose { posterUrl?.let { window.asDynamic().URL.revokeObjectURL(it) } }
    }

    posterUrl?.let { url ->
        // Anchored to the outer (relative) tile — carries its own radius
        // since the inner clipping box isn't the positioning context.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .sBorderRadius(if (compact) 8 else 10)
                .styleModifier {
                    property("inset", "0")
                    property("background", "url('$url') center/cover no-repeat")
                }
        ) {}
    }
    Box(
        modifier = Modifier
            .sSize(if (compact) 26 else 34)
            .borderRadius(50.percent)
            .backgroundColor(Color("rgba(0, 0, 0, 0.45)"))
            .zIndex(1),
        contentAlignment = Alignment.Center,
    ) {
        Icon(src = AppIcons.Play, modifier = Modifier.sSize(if (compact) 14 else 20))
    }
    com.arena_web.utils.formatDurationSeconds(duration)?.let { label ->
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .sBorderRadius(6)
                .sPadding(2, 6)
                .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
                .styleModifier {
                    property("bottom", s(4))
                    property("right", s(4))
                    property("z-index", "1")
                },
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                label,
                modifier = Modifier.sFontSize(10).color(AppColors.PrimaryWhite),
            )
        }
    }
}

@Composable
private fun FailedThumbnail(
    fail: com.arena_web.core.viewmodel.FailedMediaUpload,
    onDismiss: () -> Unit,
    compact: Boolean = false,
) {
    var previewLoaded by remember(fail.previewUrl) { mutableStateOf(fail.previewUrl == null) }
    if (!fail.isVideo && fail.previewUrl != null) {
        DisposableEffect(fail.previewUrl) {
            val img = org.w3c.dom.Image()
            val done: (Event) -> Unit = { previewLoaded = true }
            img.addEventListener("load", done)
            img.addEventListener("error", done)
            img.src = fail.previewUrl
            if (img.complete) previewLoaded = true
            onDispose {
                img.removeEventListener("load", done)
                img.removeEventListener("error", done)
            }
        }
    }
    Box(
        modifier = Modifier
            .sWidth(if (compact) 70 else 90)
            .sHeight(if (compact) 100 else 120)
            .styleModifier { property("flex-shrink", "0") }
            .position(Position.Relative),
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .sBorderRadius(if (compact) 8 else 10)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("border", "0.5px solid rgba(255, 66, 66, 0.6)")
                    property("background-color", "var(--l-s3, #0B1A38)")
                    if (!fail.isVideo && fail.previewUrl != null) {
                        property("background-image", "url('${fail.previewUrl}')")
                        property("background-size", "cover")
                        property("background-position", "center")
                        property("background-repeat", "no-repeat")
                    }
                },
            contentAlignment = Alignment.Center,
        ) {
            if (!previewLoaded) {
                com.arena_web.ui.components.widgets.LoadingSpinner(size = 24.px)
            }
            if (fail.isVideo && fail.previewUrl != null) {
                VideoPosterPreview(
                    previewUrl = fail.previewUrl,
                    compact = compact,
                    onReady = { previewLoaded = true },
                )
            }
        }

        // ✕ — dismisses this entry (same badge recipe as the picked tiles).
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .sTop(if (compact) 2.5 else 5)
                .sRight(if (compact) 3.5 else 2)
                .sSize(if (compact) 16 else 24)
                .borderRadius(50.percent)
                .cursor(Cursor.Pointer)
                .onClick { onDismiss() }
                .styleModifier {
                    property("border", "0.3px solid var(--arena-border-top)")
                    property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                    property("z-index", "1")
                },
            contentAlignment = Alignment.Center,
        ) {
            Icon(src = AppIcons.Close, modifier = Modifier.sSize(if (compact) 8 else 10))
        }
    }
}

/**
 * Sheet action pill sizing — the mobile buttons split the row 50/50 at the
 * Figma 44-px height (the desktop pills size to their 14/32 padding).
 */
private fun mobilePillModifier(): Modifier = Modifier
    .sHeight(44)
    .sPadding(0, 0)
    .styleModifier {
        property("flex", "1 1 0px")
        property("min-width", "0")
        property("box-sizing", "border-box")
    }

@Composable
private fun OutlinePillButton(
    label: String,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .sPadding(14, 32)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                if (!enabled) property("opacity", "0.5")
            }
            .onClick { if (enabled) onClick() }
            .then(modifier),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

@Composable
private fun PrimaryPillButton(
    label: String,
    enabled: Boolean,
    primaryColor: com.varabyte.kobweb.compose.ui.graphics.Color?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    /** Solid fill for the disabled state instead of the 0.5-opacity dim. */
    disabledBackground: String? = null,
) {
    val sportColors = LocalSportColors.current
    val bg = primaryColor ?: com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x09, 0x14, 0x2D)
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .sPadding(14, 32)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                property("border", "0.3px solid var(--arena-border-top)")
                if (!enabled && disabledBackground != null) {
                    property("background", disabledBackground)
                } else {
                    property("background", bg.toString())
                    if (!enabled) property("opacity", "0.5")
                }
            }
            .onClick { if (enabled) onClick() }
            .then(modifier),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier.bodyLMedium().color(
                if (primaryColor != null || (!enabled && disabledBackground != null)) {
                    sportColors.onPrimary
                } else {
                    AppColors.Text.Secondary
                }
            )
        )
    }
}
